import { useEffect, useState } from "react";
import { supabase } from "./supabase";
import { loadPaymentSettings } from "./paymentSettings";

// Admin panel for the credit-card processing fee (owner directive 2026-06-17).
// Toggle on/off + edit the card-present (terminal) and card-not-present
// (online / manual / saved card) rates. Render this only for admins.
export default function CardFeeSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("payment_settings").select("*").eq("id", 1).single();
      setS(data || { id: 1, cc_fee_enabled: false, cc_fee_present_pct: 2.7, cc_fee_notpresent_pct: 2.9, cc_fee_label: "Card Processing Fee" });
    })();
  }, []);

  if (!s) return null;

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true); setSavedMsg("");
    const payload = {
      cc_fee_enabled: !!s.cc_fee_enabled,
      cc_fee_present_pct: Math.max(0, Number(s.cc_fee_present_pct) || 0),
      cc_fee_notpresent_pct: Math.max(0, Number(s.cc_fee_notpresent_pct) || 0),
      cc_fee_label: (s.cc_fee_label || "Card Processing Fee").trim() || "Card Processing Fee",
      updated_at: new Date().toISOString(),
    };
    const { error } = await supabase.from("payment_settings").update(payload).eq("id", 1);
    setSaving(false);
    if (error) { setSavedMsg("❌ " + error.message); return; }
    await loadPaymentSettings(true); // refresh the shared cache
    setSavedMsg("✓ Saved");
    setTimeout(() => setSavedMsg(""), 2500);
  };

  const gold = "#b48c3c", border = "#e8e4df", muted = "#8a8276", text = "#2b2722";
  const inp = { padding: "8px 10px", border: "1px solid " + border, borderRadius: 7, fontSize: 13, width: 90, fontFamily: "inherit" };

  return (
    <div style={{ background: "#fff", border: "1px solid " + border, borderRadius: 12, marginBottom: 16, overflow: "hidden" }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: text }}>
          💳 Card Processing Fee {s.cc_fee_enabled ? <span style={{ color: gold }}>· ON ({(+s.cc_fee_present_pct)}% / {(+s.cc_fee_notpresent_pct)}%)</span> : <span style={{ color: muted, fontWeight: 600 }}>· OFF</span>}
        </span>
        <span style={{ color: muted, fontSize: 12 }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div style={{ padding: "4px 16px 16px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer" }}>
            <input type="checkbox" checked={!!s.cc_fee_enabled} onChange={e => set("cc_fee_enabled", e.target.checked)} style={{ width: 18, height: 18, accentColor: gold }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: text }}>Add a processing fee to every credit-card charge</span>
          </label>

          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 10, opacity: s.cc_fee_enabled ? 1 : 0.5, pointerEvents: s.cc_fee_enabled ? "auto" : "none" }}>
            <div>
              <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>Card present (terminal)</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="number" step="0.01" min="0" value={s.cc_fee_present_pct} onChange={e => set("cc_fee_present_pct", e.target.value)} style={inp} />
                <span style={{ fontSize: 13, color: text }}>%</span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>Card NOT present (online / keyed / saved)</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="number" step="0.01" min="0" value={s.cc_fee_notpresent_pct} onChange={e => set("cc_fee_notpresent_pct", e.target.value)} style={inp} />
                <span style={{ fontSize: 13, color: text }}>%</span>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontSize: 11, color: muted, marginBottom: 4 }}>Label on receipts</div>
              <input value={s.cc_fee_label || ""} onChange={e => set("cc_fee_label", e.target.value)} style={{ ...inp, width: "100%" }} placeholder="Card Processing Fee" />
            </div>
          </div>

          <div style={{ fontSize: 11, color: muted, marginTop: 12, lineHeight: 1.5 }}>
            Applies to every card charge: terminal, manual key-in, saved card, online booking deposits, shop, and memberships.
            Cash &amp; check are never charged a fee. Note: card-network rules generally prohibit surcharging <strong>debit</strong> cards and require posting a notice at checkout — confirm your state allows credit surcharging.
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
            <button onClick={save} disabled={saving} style={{ padding: "9px 20px", background: saving ? border : "linear-gradient(135deg,#b48c3c,#9a7430)", color: saving ? muted : "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {saving ? "Saving…" : "Save"}
            </button>
            {savedMsg && <span style={{ fontSize: 12, color: savedMsg.startsWith("✓") ? "#2e8b57" : "#c0392b", fontWeight: 700 }}>{savedMsg}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
